import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { shipmentLevelStatuses, StatusColorsKeys } from "../services/shipping";
import {
  ShippingDetailRecord_T,
  ShippingStatusSummary_T,
} from "../types/Shipping";
import useApi from "./useGaneshApi";

/**
 * A hook to coordinate and fetch all data needed for the Shipping Weekly page.
 * It uses `useQueries` to trigger all fetches in parallel. The underlying `useApi`
 * hook will deduplicate the actual network requests.
 */
export const useShippingDataCoordinator = (
  dateStart: string,
  dateEnd: string
) => {
  const { apiRequest } = useApi();

  const getOverviewData = (): Promise<any[]> => {
    const params = new URLSearchParams({
      dateStart,
      dateEnd,
    });
    return apiRequest<any[]>(
      `/v1/fulfillments_analytics/statuses/overview?${params.toString()}`
    ).then((result) => {
      if (result.error) throw new Error(result.error);
      return result.data || [];
    });
  };

  const getStatusDetailsData = (): Promise<ShippingDetailRecord_T[]> => {
    const params = new URLSearchParams({
      dateStart,
      dateEnd,
    });
    return apiRequest<ShippingDetailRecord_T[]>(
      `/v1/fulfillments_analytics/statuses/details?${params.toString()}`
    ).then((result) => {
      if (result.error) throw new Error(result.error);
      return result.data || [];
    });
  };

  const queries = useQueries({
    queries: [
      {
        queryKey: ["shipping", "overview-by-week", dateStart, dateEnd],
        queryFn: getOverviewData,
        staleTime: 0,
        refetchInterval: 1000 * 60 * 10,
      },
      {
        queryKey: ["shippingDetails", dateStart, dateEnd],
        queryFn: getStatusDetailsData,
        staleTime: 0,
        refetchInterval: 1000 * 60 * 10,
      },
    ],
  });

  const [overviewQuery, detailsQuery] = queries;

  // Calculate status summary from shipping details
  const statusSummary = useMemo((): ShippingStatusSummary_T[] | null => {
    const shippingDetails = detailsQuery.data;

    if (!Array.isArray(shippingDetails)) {
      return null;
    }

    const statusCounts: { [key: string]: number } = Object.fromEntries(
      shipmentLevelStatuses.map((status) => [status, 0])
    );

    shippingDetails.forEach((record: ShippingDetailRecord_T) => {
      const status: string = record.fulfillment_status || "Not started";
      if (statusCounts[status] !== undefined) {
        statusCounts[status] += 1;
      }
    });

    const totalRecords = shippingDetails.length;

    return Object.keys(statusCounts)
      .filter((status) =>
        shipmentLevelStatuses.includes(status as StatusColorsKeys)
      )
      .map((status) => ({
        status,
        count: statusCounts[status],
        percentage:
          totalRecords > 0
            ? ((statusCounts[status] / totalRecords) * 100).toFixed(2) + "%"
            : "0.00%",
      }));
  }, [detailsQuery.data]);

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isRefetching = queries.some((q) => q.isRefetching);

  return {
    // Loading states
    isLoading,
    isError,
    isRefetching,
    shippingDetailsRefetching: detailsQuery.isRefetching,
    overviewRefetching: overviewQuery.isRefetching,
    // Individual data sets
    overviewData: overviewQuery.data,
    shippingDetails: detailsQuery.data,
    statusSummary, // Calculated from shipping details

    // Individual errors
    overviewError: overviewQuery.error,
    detailsError: detailsQuery.error,
    error: queries.find((q) => q.error)?.error,

    // Timestamps
    dataUpdatedAt: Math.max(
      overviewQuery.dataUpdatedAt || 0,
      detailsQuery.dataUpdatedAt || 0
    ),

    // Refetch functions
    refetchOverview: overviewQuery.refetch,
    refetchDetails: detailsQuery.refetch,
    refetchAll: () =>
      Promise.all([overviewQuery.refetch(), detailsQuery.refetch()]),
  };
};
